import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { styled } from '@nykaa/ui-components';
import { hexToRgb } from '@nykaa/ui-components/styles/utils';
import AddToCart from '@nykaa/product-card-desktop/components/CardActions/AddToBag';
import { refreshCoupons } from '@nykaa/coupon-shared/store/action';
import { autoAppliedTracking } from '@nykaa/coupon-shared/trackings';
import { isValidObject } from '@nykaa/coupon-shared/utils';
import ActionWrapper from 'components/CardAction';
import { ProductRenderProps } from 'types/product';
import { selectCurrentSku } from 'shared/store/product/selector';
import { State } from 'shared/store/types';
import { ProductTrackingData } from 'shared/helpers/impressions/types';
import { useCouponEligibility } from 'hooks/Product/coupons';
import useShouldDisableCta from 'hooks/Product/action';

const PRODUCT_ADDED = 'Added to Bag';
interface AddToCartProps {
  pageLocation: string;
  handleShowCAB: () => void;
  videoId?: string;
  productTrackingData?: ProductTrackingData;
}

const AddToBagButton = styled.div`
  width: 222px;
  height: 48px;
  .btn-text {
    ${({ theme }) => theme.typography.buttonMedium};
  }
`;
const AddedToBag = styled.button`
  color: ${({ theme }) => theme.colors.white};
  ${({ theme }) => theme.borders.border100};
  background: ${({ theme }) => hexToRgb(theme.colors.warning, 0.7)};
  border-color: ${({ theme }) => hexToRgb(theme.colors.warning, 0.2)};
  height: 100%;
  width: 100%;
  display: flex;
  justify-content: center;
  align-items: center;
  cursor: pointer;
  position: relative;
  ${({ theme }) => theme.typography.buttonLarge};
  &:after { 
    content: "❯";
    position: absolute;
    right: 14px;
`;

function AddToCartButton({
  pageLocation,
  handleShowCAB,
  videoId,
  productTrackingData,
}: AddToCartProps) {
  const dispatch = useDispatch();
  const [showProductAdded, setShowProductAdded] = useState(false);
  const currentSku = useSelector((state: State) => selectCurrentSku(state));
  const isPro = useSelector((state: State) => state.auth.user.isPro);
  const isMultiCouponsEnabled = useCouponEligibility();
  useEffect(() => {
    let btnTimeOut: any;
    if (showProductAdded) {
      btnTimeOut = setTimeout(() => {
        setShowProductAdded(false);
      }, 5000);
    }
    return () => {
      if (btnTimeOut) {
        clearTimeout(btnTimeOut);
      }
    };
  }, [showProductAdded]);
  const handleAddToBag = (data: any) => {
    const {
      refreshedCouponsMap: refreshedCouponsObj,
      autoAppliedText,
      appliedCouponCodes,
      hasError,
    } = data || {};
    if (isMultiCouponsEnabled && isValidObject(data)) {
      autoAppliedTracking(appliedCouponCodes);
      refreshCoupons({
        refreshedCouponsObj,
        autoAppliedText,
        appliedCouponCodes,
        hasError,
      })(dispatch);
    }
    const status = Boolean(data);
    if (status) {
      setShowProductAdded(true);
    }
    handleShowCAB();
  };
  const { earlyAccessData } = currentSku || {};
  const { disablePriveProduct } = earlyAccessData || {};
  const disableCta = useShouldDisableCta();

  return (
    <ActionWrapper
      productTrackingData={productTrackingData}
      isAnalyticsTrackingEnabled
      enableSecondaryTagTracking
    >
      {({ handleAddToCart, handleSliderAction }: ProductRenderProps) => (
        <AddToBagButton>
          {!showProductAdded && (
            <AddToCart
              product={currentSku}
              pageLocation={{ pageType: pageLocation }}
              isPro={isPro}
              addToCartCallback={handleAddToCart}
              handleCallback={handleAddToBag}
              videoId={videoId}
              isMultiCouponsEnabled={isMultiCouponsEnabled}
              isDisabled={disablePriveProduct && disableCta}
            />
          )}
          {showProductAdded && (
            <AddedToBag onClick={handleSliderAction}>
              {PRODUCT_ADDED}
            </AddedToBag>
          )}
        </AddToBagButton>
      )}
    </ActionWrapper>
  );
}

export default AddToCartButton;
